"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";

import { Button, EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass } from "@/components/ui-system/select";
import { isAppHttpError } from "@/lib/api/client";
import { getPermissionsMatrix } from "@/lib/api/endpoints/permissions";
import { createPlatformRole, deletePlatformRole, listPlatformRoles, updatePlatformRole } from "@/lib/api/endpoints/platform-roles";
import type { PermissionAction, PermissionModuleDto, PlatformRoleDto, RoleModulePermissionDto } from "@/lib/api/types/enterprise";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const ACTION_COLUMNS: Array<{ key: keyof RoleModulePermissionDto; label: string }> = [
  { key: "can_view", label: "View" },
  { key: "can_create", label: "Create" },
  { key: "can_edit", label: "Edit" },
  { key: "can_delete", label: "Delete" },
  { key: "can_manage_permissions", label: "Manage permissions" },
  { key: "can_export", label: "Export" },
];

const PLATFORM_ROLE_HIDDEN_MODULES = new Set(["dashboards"]);

function platformModuleLabel(module: PermissionModuleDto): string {
  if (module.key === "users") {
    return "Customers";
  }
  if (module.key === "platform_users") {
    return "Users";
  }
  return module.label;
}

function platformModuleDescription(module: PermissionModuleDto): string {
  if (module.key === "users") {
    return "Manage customer accounts, assignments, and sign-in-as-customer access.";
  }
  if (module.key === "platform_users") {
    return "Manage internal platform operators and their access.";
  }
  return module.description ?? module.key;
}

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

function emptyPermission(moduleKey: string): RoleModulePermissionDto {
  return {
    module_key: moduleKey,
    can_view: false,
    can_create: false,
    can_edit: false,
    can_delete: false,
    can_export: false,
    can_manage_permissions: false,
  };
}

export function PlatformRolesPage() {
  const qc = useQueryClient();
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const resetWorkspaceChrome = useWorkspaceChromeStore((s) => s.resetWorkspaceChrome);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<PlatformRoleDto | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<RoleModulePermissionDto[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setWorkspaceChrome({ title: "Platform Roles", search: null });
    return () => resetWorkspaceChrome();
  }, [resetWorkspaceChrome, setWorkspaceChrome]);

  const modulesQuery = useQuery({
    queryKey: ["permissions-matrix", "platform-modules"],
    queryFn: getPermissionsMatrix,
    staleTime: 60_000,
  });
  const rolesQuery = useQuery({
    queryKey: ["platform-roles"],
    queryFn: listPlatformRoles,
    staleTime: 30_000,
  });

  const modules = useMemo(
    () => (modulesQuery.data?.modules ?? []).filter((module) => !PLATFORM_ROLE_HIDDEN_MODULES.has(module.key)),
    [modulesQuery.data?.modules],
  );
  const filteredModules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter((module) => [module.label, module.key, module.description ?? ""].join(" ").toLowerCase().includes(term));
  }, [modules, search]);

  const createMutation = useMutation({
    mutationFn: () => createPlatformRole({ name, description: description || null, permissions }),
    onSuccess: async () => {
      setDialog(null);
      setEditing(null);
      setName("");
      setDescription("");
      setPermissions([]);
      setFormError(null);
      setToast("Platform role created.");
      await qc.invalidateQueries({ queryKey: ["platform-roles"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editing) {
        throw new Error("Select a role");
      }
      return updatePlatformRole(editing.id, { name, description: description || null, permissions });
    },
    onSuccess: async () => {
      setDialog(null);
      setEditing(null);
      setName("");
      setDescription("");
      setPermissions([]);
      setFormError(null);
      setToast("Platform role updated.");
      await qc.invalidateQueries({ queryKey: ["platform-roles"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePlatformRole,
    onSuccess: async () => {
      setToast("Platform role deleted.");
      await qc.invalidateQueries({ queryKey: ["platform-roles"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setPermissions(modules.map((module) => emptyPermission(module.key)));
    setFormError(null);
    setDialog("create");
  };

  const openEdit = (role: PlatformRoleDto) => {
    setEditing(role);
    setName(role.name);
    setDescription(role.description ?? "");
    setPermissions(
      modules.map((module) => role.permissions.find((item) => item.module_key === module.key) ?? emptyPermission(module.key)),
    );
    setFormError(null);
    setDialog("edit");
  };

  const rows = rolesQuery.data?.items ?? [];

  return (
    <EnterprisePageShell hideHeader kicker="Platform" title="Platform roles" description="Define which platform modules an internal operator can access and what they can do in each one.">
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
      <ListSurface>
        <ListSurfaceHeader
          title="Platform role library"
          description="These roles are used only by platform users, not by customer workspace accounts."
          meta={<ListCount total={rows.length} filtered={rows.length} noun={rows.length === 1 ? "role" : "roles"} />}
          controls={
            <ListToolbar>
              <ListToolbarGroup grow>
                <input className={tableHeaderSearchInputClass} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search module, role, or description..." />
              </ListToolbarGroup>
              {search.trim() ? <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} /> : null}
              <ToolbarIconButton icon={Plus} label="Create role" variant="primary" onClick={openCreate} />
            </ListToolbar>
          }
        />
        <ListSurfaceBody className="overflow-x-auto">
          {rolesQuery.isLoading ? (
            <div className="p-ds-4" aria-label="Loading platform roles">
              {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={4} />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title="No platform roles found" description="Create a role to assign platform permissions to internal operators." className="rounded-none border-0 border-t" />
          ) : (
            <table className="min-w-full border-collapse">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Description</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Permissions</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((role, index) => (
                  <tr key={role.id} className="border-t border-card-border" style={{ background: index % 2 === 1 ? "rgba(59,130,246,0.05)" : "var(--bg-surface)" }}>
                    <td className="px-4 py-4 align-middle">
                      <div className="font-semibold text-foreground">{role.name}</div>
                      <div className="mt-1 text-xs text-muted">{role.key}</div>
                    </td>
                    <td className="px-4 py-4 align-middle text-sm text-muted">{role.description ?? "-"}</td>
                    <td className="px-4 py-4 align-middle text-sm text-muted">
                      {role.permissions.filter((permission) => permission.can_view || permission.can_create || permission.can_edit || permission.can_delete || permission.can_export || permission.can_manage_permissions).length} enabled modules
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <div className="flex justify-end gap-2">
                        <button type="button" className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-primary/15 bg-primary/10 px-[10px] text-primary" onClick={() => openEdit(role)} title={`Edit ${role.name}`}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-error/25 bg-error/10 px-[10px] text-error" onClick={() => deleteMutation.mutate(role.id)} title={`Delete ${role.name}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ListSurfaceBody>
      </ListSurface>

      {dialog ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-slate-950/25 p-4 backdrop-blur-sm sm:p-6">
          <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-6xl flex-col overflow-hidden rounded-card border border-card-border bg-surface-elevated shadow-overlay sm:max-h-[calc(100dvh-3rem)]">
            <div className="mb-0 flex shrink-0 items-start justify-between gap-4 border-b border-card-border px-6 py-5">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{dialog === "create" ? "Create platform role" : `Edit ${editing?.name ?? "platform role"}`}</h2>
                <p className="text-sm text-muted">Grant explicit platform modules and actions. Devices create can be granted here, unlike customer admins.</p>
              </div>
              <ToolbarIconButton icon={RotateCcw} label="Reset" onClick={() => editing ? openEdit(editing) : openCreate()} />
            </div>
            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 py-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Role name</span>
                  <input className="ui-control rounded-control px-4 py-3" value={name} onChange={(event) => setName(event.target.value)} />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Description</span>
                  <input className="ui-control rounded-control px-4 py-3" value={description} onChange={(event) => setDescription(event.target.value)} />
                </label>
              </div>
              {formError ? <div className="rounded-control border border-error/30 bg-error/10 px-3 py-2 text-caption text-error" role="alert">{formError}</div> : null}
              <div className="max-h-[55vh] overflow-auto rounded-2xl border border-card-border">
                <table className="min-w-full border-collapse">
                  <thead className="bg-[var(--bg-surface)]">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Module</th>
                      {ACTION_COLUMNS.map((column) => (
                        <th key={column.key} className="px-3 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModules.map((module: PermissionModuleDto, index) => {
                      const permission = permissions.find((item) => item.module_key === module.key) ?? emptyPermission(module.key);
                      return (
                        <tr key={module.key} className="border-t border-card-border" style={{ background: index % 2 === 1 ? "rgba(59,130,246,0.05)" : "var(--bg-surface)" }}>
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-foreground">{platformModuleLabel(module)}</div>
                            <div className="mt-1 text-xs text-muted">{platformModuleDescription(module)}</div>
                          </td>
                          {ACTION_COLUMNS.map((column) => {
                            const action = column.key.replace("can_", "") as PermissionAction;
                            const supported = module.available_actions.includes(action);
                            return (
                              <td key={column.key} className="px-3 py-3 text-center">
                                {supported ? (
                                  <input
                                    type="checkbox"
                                    checked={Boolean(permission[column.key])}
                                    onChange={(event) => {
                                      const checked = event.target.checked;
                                      setPermissions((current) => {
                                        const exists = current.some((item) => item.module_key === module.key);
                                        if (!exists) {
                                          return [...current, { ...emptyPermission(module.key), [column.key]: checked }];
                                        }
                                        return current.map((item) => item.module_key === module.key ? { ...item, [column.key]: checked } : item);
                                      });
                                    }}
                                  />
                                ) : (
                                  <span className="text-xs font-medium text-slate-300">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="sticky bottom-0 -mx-6 -mb-5 flex justify-end gap-3 border-t border-card-border bg-surface-elevated px-6 py-4">
                <button type="button" onClick={() => setDialog(null)} className="h-10 rounded-lg border border-card-border px-4 text-sm font-semibold text-foreground">Cancel</button>
                <Button type="button" variant="primary" size="md" disabled={createMutation.isPending || updateMutation.isPending} onClick={() => dialog === "create" ? createMutation.mutate() : updateMutation.mutate()}>
                  {dialog === "create" ? (createMutation.isPending ? "Creating..." : "Create role") : (updateMutation.isPending ? "Saving..." : "Save role")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </EnterprisePageShell>
  );
}
